import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import { ToastService } from "src/Shared/Services";
import CompanyOfficeService from "../../Services/CompanyOffice.service";
import MainContentBox from "src/Shared/Components/MainContentBox/MainContentBox";
import Title from "src/Shared/Components/Title/Title";
import { useStyles } from "./CompanyOfficeGrid.styles";
import { CustomDataGrid } from "src/Shared/Components";
import { officeColumns } from "./CompanyOfficeGrid.column";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { IPageRequest } from "src/Shared/Interfaces";
import { useParams } from "react-router-dom";
import { ICompanyOffice, IRouteParams } from "../../Interfaces";
import { observer } from "mobx-react";
import { GridPaginationModel } from "@mui/x-data-grid";
import { IOfficeFilter } from "../../Interfaces/IOfficeFilter";
import { useModal } from "src/Shared/Hooks";
import OfficeForm from "../AddOrEditOffices/AddOrEditOffice";
import AddOrEditOffice from "../AddOrEditOffices/AddOrEditOffice";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import ConditionalWrapper from "src/Shared/Components/ConditionalWrapper/ConditionalWrapper";
import OfficeBulkImport from "./OfficeBulkImport";
import { OFFICE_CSV_FILENAME, OFFICE_CSV_TEMPLATE } from "../../Tools/OfficeCsvUtils";
import ImperansReferencePicker from "../ImperansReference/ImperansReferencePicker";
import { useImperansReferenceList } from "../ImperansReference/useImperansReferenceList";
import { IImperansReferencePick } from "../../Interfaces/IImperansReference";

type Props = {
  subMenuRender?: boolean;
  /**
   * A submitted onboarding disables Add, Import, Edit and Remove in the wizard — the template
   * download stays available, and the client record's own Offices section is never read-only
   * (S3.10, resolved by decision 6).
   */
  readOnly?: boolean;
  /**
   * The client's country, which decides the Imperans clinic list (Epic 6303, story 6373). The wizard
   * passes it from its own state; the record page falls back to the loaded overview.
   */
  countryCode?: string | null;
  /**
   * Wizard only: every site needs the Imperans clinic that services it, the site form refuses to
   * save without one, and "Same clinic for every site" is offered (story 6373). The record's own
   * Offices page keeps saving without it.
   */
  requireServicingClinic?: boolean;
};

export const SAME_CLINIC_LABEL = "Same clinic for every site";

const CompanyOfficeGrid: React.FC<Props> = ({
  subMenuRender,
  readOnly,
  countryCode,
  requireServicingClinic = false,
}) => {
  const {
    rootStore: {
      companyOfficeStore: {
        gridLoading,
        companyOfficeList,
        getCompanyOffices,
        refreshCompanyOfficeList,
        primaryOfficeID,
      },
      // companyOverview supplies the country that biases the address lookup (S3.1).
      companyStore: { getCompanyMenu, companyOverview },
    },
  } = useStore();

  const classes = useStyles();
  const modal = useModal();

  const { companyRowID } = useParams<Partial<IRouteParams>>();
  const [pendingRemoval, setPendingRemoval] = useState<ICompanyOffice | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const resolvedCountry = countryCode ?? companyOverview?.countryCode ?? null;

  // "Same clinic for every site" (story 6373): one pick from the Imperans list, written onto every
  // site by the server. The list is fetched only where the action is offered.
  const [sameClinicOpen, setSameClinicOpen] = useState<boolean>(false);
  const [sameClinic, setSameClinic] = useState<IImperansReferencePick | null>(null);
  const [applyingClinic, setApplyingClinic] = useState<boolean>(false);
  const clinicList = useImperansReferenceList("clinics", resolvedCountry, requireServicingClinic);

  const applySameClinic = () => {
    if (!sameClinic?.id) return;
    setApplyingClinic(true);
    CompanyOfficeService.setSitesServicingClinic({
      companyRowID,
      imperansFranchiseOfficeID: sameClinic.id,
      imperansFranchiseOfficeName: sameClinic.name,
    })
      .pipe(finalize(() => setApplyingClinic(false)))
      .subscribe({
        next: (data) => {
          ToastService.success(data.message);
          setSameClinicOpen(false);
          refreshCompanyOfficeList(companyRowID, true);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };
  const [gridPagination, setGridPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const onGridPaginationChange = (data: GridPaginationModel) => {
    setGridPagination(data);
    setRequest({
      filterCollection: {
        companyRowID: companyRowID,
      },
      sortCollection: [],
      pageNumber: data.page + 1,
      pageSize: data.pageSize,
    });
  };
  const [request, setRequest] = useState<IPageRequest<IOfficeFilter>>({
    pageNumber: 1,
    pageSize: 10,
    filterCollection: {
      companyRowID: companyRowID,
    },
    sortCollection: [],
  });

  const updateOffice = (companyOffice: ICompanyOffice) => {
    modal.open(
      <AddOrEditOffice
        mode={VIEW_MODE.EDIT}
        companyOffice={companyOffice}
        companyRowID={companyRowID}
        primayOfficeId={primaryOfficeID}
        countryCode={resolvedCountry}
        requireServicingClinic={requireServicingClinic}
      />,
    );
  };

  useEffect(() => {
    getCompanyOffices(request);
  }, [request]);

  const handleOpen = () => {
    modal.open(
      <AddOrEditOffice
        mode={VIEW_MODE.ADD}
        companyRowID={companyRowID}
        primayOfficeId={primaryOfficeID}
        countryCode={resolvedCountry}
        requireServicingClinic={requireServicingClinic}
      />,
    );
  };

  const openBulkImport = () => {
    modal.open(
      <OfficeBulkImport
        companyRowID={companyRowID}
        countryCode={resolvedCountry}
        requireServicingClinic={requireServicingClinic}
      />,
    );
  };

  const downloadCsvTemplate = () => {
    const blob = new Blob([OFFICE_CSV_TEMPLATE], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = OFFICE_CSV_FILENAME;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  /** The primary-office radio moves the primary across the list (S3.5). */
  const makePrimary = (office: ICompanyOffice) => {
    CompanyOfficeService.updateOffice({ ...office, isPrimaryOffice: true }).subscribe({
      next: () => refreshCompanyOfficeList(companyRowID, true),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  /**
   * Removing a site warns that staff access will be cleared, because the server strips the site
   * from every contact's access, every narrowed notification scope and every Xero key allocation
   * (S3.8/S3.9). Cancelling changes nothing.
   */
  const removeOffice = (office: ICompanyOffice) => setPendingRemoval(office);

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    setRemoving(true);
    CompanyOfficeService.deleteOffice(pendingRemoval.companySiteID)
      .pipe(finalize(() => setRemoving(false)))
      .subscribe({
        next: (data) => {
          ToastService.success(data.message);
          setPendingRemoval(null);
          refreshCompanyOfficeList(companyRowID, true);
          getCompanyMenu(companyRowID, false);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  return (
    <>
      <ConditionalWrapper
        condition={!subMenuRender}
        wrapper={(children) => (
          <MainContentBox>
            <Title warningRequired title="Client Offices"></Title>
            {children}
          </MainContentBox>
        )}
      >
        <Box className={subMenuRender ? classes.root_1 : classes.root}>
          {subMenuRender && (
            <Typography className={classes.title} variant="h5">
              {/* The count sits beside the section title (S3.3). */}
              Client Offices ({companyOfficeList.totalNumberOfRecords || 0})
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={downloadCsvTemplate}>
              Download CSV Template
            </Button>
            <Button variant="outlined" disabled={readOnly} onClick={openBulkImport}>
              Bulk Import
            </Button>
            {requireServicingClinic && (
              <Button
                variant="outlined"
                disabled={readOnly || !(companyOfficeList.totalNumberOfRecords || 0)}
                onClick={() => setSameClinicOpen(true)}
              >
                {SAME_CLINIC_LABEL}
              </Button>
            )}
            <Button variant="contained" disabled={readOnly} onClick={handleOpen}>
              Add Offices
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <CustomDataGrid
            rowHeight={64}
            loading={gridLoading}
            columns={officeColumns(updateOffice, makePrimary, removeOffice, readOnly, requireServicingClinic)}
            rows={companyOfficeList.results || []}
            rowCount={companyOfficeList.totalNumberOfRecords || 0}
            rowSelection={false}
            getRowId={(row: ICompanyOffice) => row.companySiteID}
            style={{
              height: "calc(100vh - 245px)",
            }}
            paginationModel={gridPagination}
            onPaginationModelChange={(model, _) =>
              onGridPaginationChange(model)
            }
            onRowDoubleClick={(params) => !readOnly && updateOffice(params.row)}
          />
        </Box>
      </ConditionalWrapper>

      {/* One Imperans clinic for every site (story 6373); the server writes it onto each of them. */}
      <Dialog open={sameClinicOpen} onClose={() => setSameClinicOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{SAME_CLINIC_LABEL}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Every site this client has will be serviced by the clinic you pick here, replacing any
            clinic already chosen on a site. The list is the paired Imperans's, most used first, and
            is not limited to the client's own franchise.
          </DialogContentText>
          <ImperansReferencePicker
            label="Servicing clinic"
            list={clinicList}
            hasCountry={Boolean(resolvedCountry)}
            value={sameClinic}
            onChange={setSameClinic}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSameClinicOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!sameClinic?.id || applyingClinic}
            onClick={applySameClinic}
          >
            Apply to every site
          </Button>
        </DialogActions>
      </Dialog>

      {/* The confirmation names the site and warns that staff access will be cleared (S3.8). */}
      <Dialog open={Boolean(pendingRemoval)} onClose={() => setPendingRemoval(null)}>
        <DialogTitle>Remove {pendingRemoval?.officeName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Removing “{pendingRemoval?.officeName}” clears it from every contact's site access, from
            any narrowed result notifications, and from any Xero key scoped to it. A contact left
            with no sites goes back to covering all of them, and a Xero key left with no offices
            will need re-scoping before Invoicing can be completed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={removing} onClick={confirmRemoval}>
            Remove site
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default observer(CompanyOfficeGrid);
CompanyOfficeGrid.defaultProps = {
  subMenuRender: false,
};
